import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import BlogFooter from 'components/BlogFooter';
import styles from './styles.module.scss';

const BLOG_SLUG = 'nobody-owns-the-birthday';
const ARTICLE_URL = `https://trytrivia.co/blog/${BLOG_SLUG}`;

const NobodyOwnsBirthday = () => (
  <main className={styles.blogPost}>
    <Helmet>
      <title>Nobody Owns Birthdays. That&apos;s Why They Get Missed | Trivia</title>
      <meta
        name='description'
        content='When the person who posts in #general is OOO, the anniversary still happens. Missed milestones are a belonging failure, not a sheet problem.'
      />
      <meta property='og:title' content="Nobody Owns Birthdays. That's Why They Get Missed" />
      <meta
        property='og:description'
        content='Missed milestones are a belonging failure, not a spreadsheet problem.'
      />
      <meta property='og:url' content={ARTICLE_URL} />
      <link rel='canonical' href={ARTICLE_URL} />
    </Helmet>

    <header className={styles.navigation}>
      <div className={styles.navigationInner}>
        <Link className={styles.logo} to='/blog'>
          <span className={styles.logoMark}>?</span>
          <span>Trivia</span>
        </Link>
        <nav className={styles.navLinks} aria-label='Main navigation'>
          <a href='https://trytrivia.co/slack/'>Slack</a>
          <a href='https://trytrivia.co/microsoft-teams/'>Teams</a>
          <a href='https://trytrivia.co/'>Home</a>
          <Link className={styles.loginLink} to='/login'>
            Get started
          </Link>
        </nav>
      </div>
    </header>

    <article>
      <header className={styles.articleHeader}>
        <Link className={styles.backLink} to='/blog'>
          ← Back to all articles
        </Link>
        <p className={styles.category}>Employee milestones</p>
        <h1>Nobody Owns the Birthday. That&apos;s Why It Gets Missed.</h1>
        <p className={styles.intro}>
          When the person who posts in #general is OOO, the anniversary still happens. Missed
          milestones are a belonging failure, not a sheet problem.
        </p>
        <div className={styles.meta}>
          <span>Casey / TryTrivia Content</span>
          <span>September 1, 2026</span>
          <span>7 min read</span>
        </div>
      </header>

      <div className={styles.articleBody}>
        <p>Jordan&apos;s one-year is Tuesday. The sheet says Alex in People Ops posts in #general by 10am.</p>
        <p>Alex is in Lisbon. Phone on airplane mode.</p>
        <p>The day happens anyway.</p>
        <p>
          At 4:51pm someone in Support types &quot;wait was it today.&quot; Two cake emojis. One delayed
          &quot;congrats!!&quot; from a manager on the train. Jordan is still in tickets. The person who
          started in March is watching. Their 90 days land next week. They just learned how this company
          treats dates.
        </p>
        <p>
          That is not a spreadsheet miss. That is an ownership gap. Culture was a side quest. Nobody
          owned it, so the milestone became a lottery.
        </p>
        <p>
          HR does not need a better reminder. You need the channel to notice without a human being
          awake, guilty, or in the country.
        </p>

        <h2>Culture as a side quest</h2>
        <p>
          Ask a manager who owns birthdays. They will say People Ops. Ask People Ops. They will say
          the manager, &quot;because they know the person.&quot; Ask the office manager. There isn&apos;t one
          anymore. There is a tab named Dates, last touched in 2024, and a rotating sense of dread.
        </p>
        <p>Whoever feels guilty that week is the owner. Guilt is not an operating system.</p>
        <p>
          This is how hybrid culture actually fails. A task that sounds small, lives in a sheet, and
          only works if the designated human is online, not in interviews, not on PTO.
        </p>
        <p>
          You already run payroll without hoping the right person remembers. You do not run belonging
          that way. You hope.
        </p>

        <h3>A missed date is a belonging failure</h3>
        <p>
          You can survive a missed cake. You cannot shrug off being the person whose year of work did
          not show up in the channel.
        </p>
        <p>
          It hits harder if you are new, remote, or on a team that already feels like a satellite
          office. The signal is simple: we notice you when it is convenient. If you started in March
          and watched Jordan&apos;s year pass in silence, you did not learn a process. You learned your
          rank.
        </p>
        <p>
          Recognition is how people check whether they are real here. When the cue depends on one
          person&apos;s Tuesday, some people get seen and some people get the 4:51pm &quot;wait was it
          today.&quot;
        </p>

        <h3>The guilt ping is worse than quiet</h3>
        <p>Then People Ops tries to save it.</p>
        <p>
          8:12am Slack DM: &quot;Hey, can someone wish Jordan happy work anniversary in #general? I am OOO
          but it is in the sheet.&quot;
        </p>
        <p>
          Now the company is watching the scramble. Jordan can see it too. Care becomes a chore HR
          chases. Managers learn that if they ignore the ping, someone else will panic-post.
        </p>
        <p>A reminder from People Ops is not warmth. It is a ticket with a cake emoji on it.</p>

        <h2>The shoutout can be boring. The thread should not be.</h2>
        <p>
          People get precious about this. &quot;It should feel human.&quot; Agreed. Human is the teammate who
          knows Jordan hates surprise video calls and loves a stupid GIF. Human is the thread.
        </p>
        <p>The trigger can be boring. It should be.</p>
        <p>
          A channel post that lands on the day, without a hero, is not colder than a scramble. It is
          more honest. The warmth is supposed to come after the cue, from people, not from whoever
          remembered the sheet.
        </p>
        <p>
          <strong>Tuesday A.</strong> HR ping at 8:12. Three delayed notes. One private &quot;so sorry, I was
          in back to backs.&quot; Jordan replies &quot;all good!!&quot; which means it is not.
        </p>
        <p>
          <strong>Tuesday B.</strong> A post hits the channel in the morning. People pile on because they
          saw it, not because they were assigned. Someone who actually works with Jordan adds the
          specific thing. The person who started in March taps a reaction and feels like they belong
          in the room.
        </p>
        <p>Tuesday B is not fake. Tuesday A is theater with a lag.</p>
        <p>
          You can still do lunch, a card, a gift. Do not delay the public &quot;we see you&quot; until the
          perfect version is staffed.
        </p>

        <h2>What has to be true</h2>
        <ol>
          <li>Dates cannot live in a file only one person can find.</li>
          <li>The cue cannot require a human to be online, not OOO, and in the right time zone.</li>
          <li>The person being named can opt out. Birthdays are not a values test.</li>
          <li>
            The post lives where people already work:{' '}
            <a href='https://trytrivia.co/slack/'>Slack</a> or{' '}
            <a href='https://trytrivia.co/microsoft-teams/'>Microsoft Teams</a>. Not a recognition
            platform nobody opened after the kickoff.
          </li>
        </ol>
        <p>
          If your system fails any of those, you do not have a celebration program. You have a hope.
        </p>
        <p>
          <a href='https://trytrivia.co/employee-milestones/'>TryTrivia Celebrations</a> is the boring
          version of this. The bot asks people for a birthday and a work anniversary. On the day, it
          posts in the channel you picked, with a GIF. People can opt out. You pick a time. You are not
          the birthday captain. Near-zero admin after that. The install{' '}
          <a href='https://trytrivia.co/'>starts free</a>. Paid is for bigger channels or{' '}
          <a href='https://trytrivia.co/games/custom-quiz/'>custom quizzes</a>, not for the right to
          be wished on time.
        </p>
        <p>
          If the team actually knows the person, a short quiz about them can sit next to the shoutout.
        </p>
        <p>
          A bot cannot make anyone care. It can stop you from being the single point of failure. That
          is the job.
        </p>

        <h2>Stop staffing a birthday captain</h2>
        <p>
          Do not put People Ops on a rotation for cake-equivalent posts. That rotation is how Jordan got
          silence.
        </p>
        <p>Keep dates in HRIS if you must. Do not make #general depend on a tab named Dates.</p>
        <p>
          Pick a channel people already open. <code>#general</code> if humans still talk there. A team
          channel if #general is announcements-only. Do not invent <code>#birthdays-and-vibes</code>.
        </p>
        <p>
          The automated post is the cue. Managers can add the specific thing in the thread: the
          project, the joke, the &quot;you made Tuesday standups less bleak.&quot; If they wait to &quot;do it
          properly,&quot; they will miss it.
        </p>
        <p>Some people do not want a public birthday. Build the off ramp. Then leave them alone.</p>

        <h2>What to kill</h2>
        <ul>
          <li>Public &quot;who forgot Jordan&quot; energy. Shame is not recognition.</li>
          <li>Saving the shoutout for the all-hands so it feels bigger. It feels like a backlog.</li>
          <li>Surprise video singing.</li>
          <li>Using the day as a review sandwich.</li>
          <li>An 8:12am People Ops DM asking the company to perform care on a deadline.</li>
        </ul>
        <p>
          If a VP wants a &quot;real&quot; celebration, they can buy lunch. They cannot veto the Tuesday cue
          because it came from a bot.
        </p>

        <h2>A note you can send when someone says &quot;we should just remember&quot;</h2>
        <blockquote>
          We are not assigning a birthday captain. Dates will not depend on who is in the office or
          who is OOO. A post will hit the team channel on the day. Add a real note in the thread if
          you have one. Skip it if you do not. People can opt out. We will not chase #general from a
          spreadsheet, and we will not ping the company at 8am to perform warmth.
        </blockquote>
        <p>If they still want a card, send a card. Keep it off the critical path.</p>

        <h2>This week, not after the next miss</h2>
        <p>
          You do not need a recognition philosophy. You need Tuesday to work when Alex is on a plane.
        </p>
        <p>
          Add TryTrivia in <a href='https://trytrivia.co/slack/'>Slack</a> or{' '}
          <a href='https://trytrivia.co/microsoft-teams/'>Microsoft Teams</a>. Turn on{' '}
          <a href='https://trytrivia.co/employee-milestones/'>Celebrations</a>. Let the bot collect
          the dates. Pick the channel. Walk away. If you want a walkthrough,{' '}
          <a href='https://calendly.com/heronhr-info'>book a short demo</a>.
        </p>
        <p>
          The new hire who started in March is still watching how you treat dates. So is Jordan. So is
          the person whose anniversary is the Thursday you are out.
        </p>
        <p>
          A bot shoutout is not the whole of care. It is the part that should never have been a side
          quest.
        </p>

        <div className={styles.related}>
          <p className={styles.category}>Related links</p>
          <div>
            <a href='https://trytrivia.co/employee-milestones/'>Employee milestones</a>
            <a href='https://trytrivia.co/slack/'>Trivia for Slack</a>
            <a href='https://trytrivia.co/microsoft-teams/'>Trivia for Microsoft Teams</a>
            <a href='https://trytrivia.co/games/custom-quiz/'>Custom quizzes</a>
            <a href='https://trytrivia.co/games/'>Games</a>
          </div>
        </div>
      </div>
    </article>

    <BlogFooter />
  </main>
);

export default NobodyOwnsBirthday;
